import { useEffect, useRef, useState } from "react";

const useDarkMode = () => {
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        setIsDark(mediaQuery.matches);

        const handler = (e) => setIsDark(e.matches);
        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
    }, []);

    return isDark;
};

const GlassSurface = ({
    children,
    width = 200,
    height = 80,
    borderRadius = 20,
    borderWidth = 0.07,
    brightness = 50,
    opacity = 0.93,
    blur = 11,
    displace = 0,
    backgroundOpacity = 0,
    saturation = 1,
    distortionScale = -180,
    redOffset = 0,
    greenOffset = 10,
    blueOffset = 20,
    xChannel = "R",
    yChannel = "G",
    mixBlendMode = "difference",
    className = "",
    style = {},
}) => {
    const containerRef = useRef(null);
    const isDarkMode = useDarkMode();

    const generateDisplacementMap = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        const actualWidth = rect?.width || 400;
        const actualHeight = rect?.height || 200;
        const edgeSize = Math.min(actualWidth, actualHeight) * (borderWidth * 0.5);

        const svgContent = `
      <svg viewBox="0 0 ${actualWidth} ${actualHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="red-grad" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="red"/>
          </linearGradient>
          <linearGradient id="blue-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="blue"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" fill="black"></rect>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#red-grad)" />
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#blue-grad)" style="mix-blend-mode: ${mixBlendMode}" />
        <rect x="${edgeSize}" y="${edgeSize}" width="${actualWidth - edgeSize * 2}" height="${actualHeight - edgeSize * 2}" rx="${borderRadius}" fill="hsl(0 0% ${brightness}% / ${opacity})" style="filter:blur(${blur}px)" />
      </svg>
    `;

        return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
    };

    const updateDisplacementMap = () => {
        const feImage = document.getElementById("glass-feimage");
        if (feImage) {
            feImage.setAttribute("href", generateDisplacementMap());
        }
    };

    useEffect(() => {
        updateDisplacementMap();
        [
            { id: "redchannel", offset: redOffset },
            { id: "greenchannel", offset: greenOffset },
            { id: "bluechannel", offset: blueOffset },
        ].forEach(({ id, offset }) => {
            const elem = document.getElementById(id);
            if (elem) {
                elem.setAttribute(
                    "scale",
                    (distortionScale + offset).toString()
                );
                elem.setAttribute("xChannelSelector", xChannel);
                elem.setAttribute("yChannelSelector", yChannel);
            }
        });

        const gaussianBlur = document.getElementById("glass-gaussian-blur");
        if (gaussianBlur) {
            gaussianBlur.setAttribute("stdDeviation", displace.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        width,
        height,
        borderRadius,
        borderWidth,
        brightness,
        opacity,
        blur,
        displace,
        distortionScale,
        redOffset,
        greenOffset,
        blueOffset,
        xChannel,
        yChannel,
        mixBlendMode,
    ]);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            setTimeout(updateDisplacementMap, 0);
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            setTimeout(updateDisplacementMap, 0);
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTimeout(updateDisplacementMap, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, height]);

    const supportsSVGFilters = () => {
        const isWebkit =
            /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
        const isFirefox = /Firefox/.test(navigator.userAgent);

        if (isWebkit || isFirefox) {
            return false;
        }

        const div = document.createElement("div");
        div.style.backdropFilter = "url(#test)";
        return div.style.backdropFilter !== "";
    };

    const supportsBackdropFilter = () => {
        if (typeof window === "undefined") return false;
        return CSS.supports("backdrop-filter", "blur(10px)");
    };

    const getContainerStyles = () => {
        const baseStyles = {
            ...style,
            width: typeof width === "number" ? `${width}px` : width,
            height: typeof height === "number" ? `${height}px` : height,
            borderRadius: `${borderRadius}px`,
            "--glass-frost": backgroundOpacity,
            "--glass-saturation": saturation,
        };

        const svgSupported = supportsSVGFilters();
        const backdropFilterSupported = supportsBackdropFilter();

        if (svgSupported) {
            return {
                ...baseStyles,
                background: isDarkMode
                    ? `hsl(0 0% 0% / ${backgroundOpacity})`
                    : `hsl(0 0% 100% / ${backgroundOpacity})`,
                backdropFilter: `url(#glass-filter) saturate(${saturation})`,
                boxShadow: isDarkMode
                    ? `0 0 2px 1px color-mix(in oklch, white, transparent 65%) inset,
             0 0 10px 4px color-mix(in oklch, white, transparent 85%) inset,
             0px 4px 16px rgba(17, 17, 26, 0.05),
             0px 8px 24px rgba(17, 17, 26, 0.05),
             0px 16px 56px rgba(17, 17, 26, 0.05),
             0px 4px 16px rgba(17, 17, 26, 0.05) inset,
             0px 8px 24px rgba(17, 17, 26, 0.05) inset,
             0px 16px 56px rgba(17, 17, 26, 0.05) inset`
                    : `0 0 2px 1px color-mix(in oklch, black, transparent 85%) inset,
             0 0 10px 4px color-mix(in oklch, black, transparent 90%) inset,
             0px 4px 16px rgba(17, 17, 26, 0.05),
             0px 8px 24px rgba(17, 17, 26, 0.05),
             0px 16px 56px rgba(17, 17, 26, 0.05),
             0px 4px 16px rgba(17, 17, 26, 0.05) inset,
             0px 8px 24px rgba(17, 17, 26, 0.05) inset,
             0px 16px 56px rgba(17, 17, 26, 0.05) inset`,
            };
        } else {
            if (isDarkMode) {
                if (!backdropFilterSupported) {
                    return {
                        ...baseStyles,
                        background: "rgba(0, 0, 0, 0.4)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.2),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.1)`,
                    };
                } else {
                    return {
                        ...baseStyles,
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(12px) saturate(1.8) brightness(1.2)",
                        WebkitBackdropFilter: "blur(12px) saturate(1.8) brightness(1.2)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.2),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.1)`,
                    };
                }
            } else {
                if (!backdropFilterSupported) {
                    return {
                        ...baseStyles,
                        background: "rgba(255, 255, 255, 0.4)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.5),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.3)`,
                    };
                } else {
                    return {
                        ...baseStyles,
                        background: "rgba(255, 255, 255, 0.25)",
                        backdropFilter: "blur(12px) saturate(1.8) brightness(1.1)",
                        WebkitBackdropFilter: "blur(12px) saturate(1.8) brightness(1.1)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        boxShadow: `0 8px 32px 0 rgba(31, 38, 135, 0.2),
                        0 2px 16px 0 rgba(31, 38, 135, 0.1),
                        inset 0 1px 0 0 rgba(255, 255, 255, 0.4),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.2)`,
                    };
                }
            }
        }
    };

    const glassSurfaceClasses =
        "relative flex items-center justify-center overflow-hidden transition-opacity duration-[260ms] ease-out";

    const focusVisibleClasses = isDarkMode
        ? "focus-visible:outline-2 focus-visible:outline-[#0A84FF] focus-visible:outline-offset-2"
        : "focus-visible:outline-2 focus-visible:outline-[#007AFF] focus-visible:outline-offset-2";

    return (
        <div
            ref={containerRef}
            className={`${glassSurfaceClasses} ${focusVisibleClasses} ${className}`}
            style={getContainerStyles()}
        >
            <svg
                className="w-full h-full pointer-events-none absolute inset-0 opacity-0 -z-10"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <filter
                        id="glass-filter"
                        colorInterpolationFilters="sRGB"
                        x="0%"
                        y="0%"
                        width="100%"
                        height="100%"
                    >
                        <feImage
                            id="glass-feimage"
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            preserveAspectRatio="none"
                            result="map"
                        />

                        <feDisplacementMap
                            id="redchannel"
                            in="SourceGraphic"
                            in2="map"
                            result="dispRed"
                        />
                        <feColorMatrix
                            in="dispRed"
                            type="matrix"
                            values="1 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
                            result="red"
                        />

                        <feDisplacementMap
                            id="greenchannel"
                            in="SourceGraphic"
                            in2="map"
                            result="dispGreen"
                        />
                        <feColorMatrix
                            in="dispGreen"
                            type="matrix"
                            values="0 0 0 0 0
                      0 1 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
                            result="green"
                        />

                        <feDisplacementMap
                            id="bluechannel"
                            in="SourceGraphic"
                            in2="map"
                            result="dispBlue"
                        />
                        <feColorMatrix
                            in="dispBlue"
                            type="matrix"
                            values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 1 0 0
                      0 0 0 1 0"
                            result="blue"
                        />

                        <feBlend in="red" in2="green" mode="screen" result="rg" />
                        <feBlend in="rg" in2="blue" mode="screen" result="output" />
                        <feGaussianBlur
                            id="glass-gaussian-blur"
                            in="output"
                            stdDeviation="0.7"
                        />
                    </filter>
                </defs>
            </svg>

            <div className="w-full h-full flex items-center justify-center p-2 rounded-[inherit] relative z-10">
                {children}
            </div>
        </div>
    );
};

export default GlassSurface;
